import {
  parseApiErrorFromResponse,
  toProviderIssueError,
} from "@/lib/api-errors";
import { env } from "@/env";
import z from "zod/v4";
import { FxCurrencyCodeSchema } from "@/lib/currencies";

/**
 * How a merchant sees wholesale FX fees, set per merchant in Orbit.
 * - `rate_plus_fee` (default): the marked rate plus a separate fixed-fee line.
 * - `all_in`: one effective rate (received ÷ total paid), no fee line.
 */
export const FeeDisplaySchema = z.enum(["rate_plus_fee", "all_in"]);
export type FeeDisplay = z.infer<typeof FeeDisplaySchema>;

export const ApplicationConfigResponseSchema = z.object({
  enabled: z.boolean(),
  // Older backends omit it; an unknown value falls back to the default display.
  feeDisplay: FeeDisplaySchema.optional().catch(undefined),
});

export type ApplicationConfigResponse = z.infer<
  typeof ApplicationConfigResponseSchema
>;

export const FxDestinationBeneficiarySchema = z.object({
  walletAddress: z.string().optional(),
  network: z.string().optional(),
  id: z.string(),
  currency: FxCurrencyCodeSchema,
  accountName: z.string(),
  accountNumber: z.string(),
  nationalId: z.string(),
  bankName: z.string(),
  payoutRail: z.enum(["local", "swift"]).nullable().optional(),
});
export type FxDestinationBeneficiary = z.infer<
  typeof FxDestinationBeneficiarySchema
>;

export const FxEligibilityReasonSchema = z.enum([
  "NO_DESTINATION_WALLET",
  "MULTIPLE_DESTINATION_WALLETS",
  "NO_ACTIVE_BENEFICIARY",
]);
export type FxEligibilityReason = z.infer<typeof FxEligibilityReasonSchema>;

export const SourceWalletEligibilityReasonSchema = z.enum([
  "NO_ELIGIBLE_DESTINATION_CORRIDOR",
]);
export type SourceWalletEligibilityReason = z.infer<
  typeof SourceWalletEligibilityReasonSchema
>;

export const FxEligibilityResponseSchema = z.object({
  wholesaleCorridors: z
    .array(
      z.object({
        sourceCurrency: z.string(),
        destinationCurrency: z.string(),
        beneficiaryIds: z.array(z.string()),
        sourceWalletIds: z.array(z.string()),
      }),
    )
    .optional(),
  applicationId: z.string(),
  provider: z.enum(["verto-fx", "mocked"]),
  sourceCurrencies: z.array(FxCurrencyCodeSchema),
  destinationCurrencies: z.array(FxCurrencyCodeSchema),
  /** All provider-supported corridors (for rates); independent of eligible trading subset. */
  supportedCurrencyPairs: z
    .array(
      z.object({
        source: FxCurrencyCodeSchema,
        destination: FxCurrencyCodeSchema,
      }),
    )
    .default([]),
  sourceWalletOptions: z.array(
    z.object({
      internalWalletId: z.string(),
      providerWalletId: z.number(),
      /** @deprecated Use internalWalletId / providerWalletId. */
      walletId: z.string().optional(),
      type: z.enum(["standard", "fx_balance"]),
      currency: FxCurrencyCodeSchema,
      fundingMode: z.enum(["AUTO_PULL", "PRE_FUNDED"]),
      availableBalance: z.number(),
      displayLabel: z.string(),
      eligible: z.boolean(),
      reasons: z.array(SourceWalletEligibilityReasonSchema),
    }),
  ),
  destinationAvailability: z.array(
    z.object({
      beneficiaryOnly: z.boolean().optional(),
      currency: FxCurrencyCodeSchema,
      eligible: z.boolean(),
      reasons: z.array(FxEligibilityReasonSchema),
      destinationWalletId: z.string().nullable().optional(),
    }),
  ),
  destinationBeneficiaries: z.array(FxDestinationBeneficiarySchema).default([]),
});
export type FxEligibilityResponse = z.infer<typeof FxEligibilityResponseSchema>;

export class ApplicationConfigNotFoundError extends Error {
  constructor() {
    super("Application not found");
    this.name = "ApplicationConfigNotFoundError";
  }
}

export const applicationApi = {
  // Get application configuration (user-facing: enabled only)
  getApplicationConfig: async (
    applicationId: string,
    accessToken: string,
  ): Promise<ApplicationConfigResponse> => {
    const response = await fetch(
      `${env.VITE_MERCHANT_PORTAL_BACKEND_URL}/api/application/${applicationId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "x-application-id": applicationId,
        },
      },
    );

    if (!response.ok) {
      if (response.status === 404) {
        // Backward compatibility for older backend behavior:
        // missing config used to return 404, now it returns 200 { enabled: false }.
        return { enabled: false };
      }
      const parsed = await parseApiErrorFromResponse(response);
      throw new Error(parsed.message || "Failed to fetch application config");
    }

    const json = await response.json();
    return ApplicationConfigResponseSchema.parse(json);
  },

  getFxEligibility: async (
    applicationId: string,
    accessToken: string,
  ): Promise<FxEligibilityResponse> => {
    const response = await fetch(
      `${env.VITE_MERCHANT_PORTAL_BACKEND_URL}/api/v2/application/${applicationId}/fx-eligibility`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "x-application-id": applicationId,
        },
      },
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new ApplicationConfigNotFoundError();
      }
      const parsed = await parseApiErrorFromResponse(response);
      const providerIssueError = toProviderIssueError(parsed);
      if (providerIssueError) {
        throw providerIssueError;
      }
      throw new Error(parsed.message || "Failed to fetch FX eligibility");
    }

    const json = await response.json();
    return FxEligibilityResponseSchema.parse(json);
  },
};
